import { Button, CircularProgress, Input, Modal } from "@shared/ui";
import { Search, UserMinus, UserPlus } from "lucide-react";
import { useEffect } from "react";
import {
  getManagerInitials,
  useCampaignManagers,
} from "../hooks/useCampaignManagers";
import styles from "./CampaignManagersModal.module.scss";

const AVATAR_COLORS = [
  styles.avatarPurple,
  styles.avatarSky,
  styles.avatarGreen,
  styles.avatarAmber,
  styles.avatarPink,
];

export type CampaignManagersModalProps = {
  open: boolean;
  onClose: () => void;
};

export function CampaignManagersModal({
  open,
  onClose,
}: CampaignManagersModalProps) {
  const {
    managerSearch,
    setManagerSearch,
    clearSearch,
    selectedManagers,
    availableToAdd,
    isFetchingEmployees,
    isUpdatingOrganizationAllocation,
    handleAdd,
    handleRemove,
  } = useCampaignManagers({ searchEnabled: open });

  useEffect(() => {
    if (!open) {
      clearSearch();
    }
  }, [open, clearSearch]);

  const handleClose = () => {
    clearSearch();
    onClose();
  };

  const showSearchResults = managerSearch.trim().length > 0;

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={460}
      destroyOnClose
      title={
        <div className={styles.modalTitleBlock}>
          <div className={styles.modalTitle}>Campaign team</div>
          <div className={styles.modalSubtitle}>
            People who can edit this campaign
          </div>
        </div>
      }
      className={styles.modal}
    >
      <div
        className={styles.body}
        aria-busy={isUpdatingOrganizationAllocation}
      >
        {isUpdatingOrganizationAllocation ? (
          <div className={styles.updatingOverlay}>
            <CircularProgress size="small" />
          </div>
        ) : null}

        <div className={styles.field}>
          <label className={styles.label}>Add people</label>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>
              <Search size={15} />
            </span>
            <Input
              autoFocus
              className={styles.searchInput}
              value={managerSearch}
              onChange={(e) => setManagerSearch(e.target.value)}
              placeholder="Search by name or email"
              allowClear
            />
          </div>
        </div>

        {showSearchResults ? (
          <div className={styles.searchResults}>
            {isFetchingEmployees ? (
              <div className={styles.listLoader}>
                <CircularProgress size="small" />
              </div>
            ) : availableToAdd.length > 0 ? (
              availableToAdd.map((user) => {
                const name = `${user.firstName} ${user.lastName}`.trim();
                return (
                  <button
                    key={user._id}
                    type="button"
                    className={styles.addRow}
                    onClick={() => {
                      handleAdd(user._id);
                      clearSearch();
                    }}
                    disabled={isUpdatingOrganizationAllocation}
                  >
                    <div className={styles.addAvatar}>
                      {getManagerInitials(name)}
                    </div>
                    <div className={styles.userText}>
                      <div className={styles.userName}>{name}</div>
                      <div className={styles.userSub}>
                        {user.emailId || "—"}
                      </div>
                    </div>
                    <UserPlus size={14} className={styles.addIcon} />
                  </button>
                );
              })
            ) : (
              <p className={styles.emptySearch}>No people found</p>
            )}
          </div>
        ) : null}

        <div>
          <div className={styles.sectionLabel}>
            On this campaign ({selectedManagers.length})
          </div>
          {selectedManagers.length === 0 ? (
            <div className={styles.emptySelected}>
              No teammates yet. Search above to add someone.
            </div>
          ) : (
            <div className={styles.selectedList}>
              {selectedManagers.map((manager, index) => (
                <div key={manager.id} className={styles.selectedRow}>
                  <div
                    className={`${styles.selectedAvatar} ${AVATAR_COLORS[index % AVATAR_COLORS.length]}`}
                  >
                    {getManagerInitials(manager.name)}
                  </div>
                  <div className={styles.userText}>
                    <div className={styles.userName}>{manager.name}</div>
                    <div className={styles.userSub}>{manager.email}</div>
                  </div>
                  <Button
                    type="text"
                    className={styles.removeButton}
                    icon={<UserMinus size={14} />}
                    onClick={() => handleRemove(manager.id)}
                    disabled={isUpdatingOrganizationAllocation}
                    title="Remove"
                    aria-label="Remove"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button type="primary" className={styles.doneButton} onClick={handleClose}>
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
